/**
 * `useResolvedAddressOverlaysBatch` — resolve the overlay bundles for a SET of
 * `(chain, address)` refs in one call, and hand back the shared
 * `resolveOverlay(address, chainId)` seam the transpose adapters expect.
 *
 * This is the "second call" the transaction / graph surfaces make after the tx
 * loads: the header seeds the leading from/to inline, then every other address
 * off the decoded body / interaction graph is enriched here in one batched
 * request (leading-only) rather than N per-address reads. Gated centrally by the
 * per-resource {@link overlayConfig} (`resolvedView`), so the whole surface goes
 * dark from one config switch. REST-only (the batch endpoint has no GraphQL
 * analog), but that no longer means un-widenable: the route takes a tenancy tier
 * of its own, so this opens at the caller's configured default view scope like
 * every other read. It did not, once, and the result was the same address showing
 * different attribution in a table than on its own detail page — with the
 * narrower one reading as an address nobody had attributed.
 */
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@authe/ui-core/api";
import type { AddressOverlay } from "@authe/fe-commons/transpose";
import type { AddressScope } from "@authe/fe-commons/graphql";
import { overlayConfig, type AttributionResource } from "@/config";
import { useScopePrefs } from "@/app/scopePrefs";
import {
  type BatchOverlays,
  type OverlayRef,
  EMPTY_BATCH_OVERLAYS,
  dedupeRefs,
  fetchAddressOverlaysBatch,
  lookupOverlays,
  refsCacheKey,
} from "@authe/fe-commons/overlays";
import { overlayToAddressDecoration } from "./overlayDecoration";

export interface UseResolvedAddressOverlaysBatchOptions {
  /** Which resource's config gates this view. Default `"address"`. */
  resource?: AttributionResource;
  /** Extra caller gate on top of the config. Default true. */
  enabled?: boolean;
  /** Resolve stored logo keys → URLs for node icons (pass `catalog.resolveLogoUrl`). */
  resolveLogoUrl?: (key: string) => string | undefined;
  /**
   * Also fetch each address's stored row (`lookupAddressRow`) — its risk, type
   * and primary entity. Costs a read per address, so only where something draws
   * them.
   *
   * Part of the cache key, because the two payloads are not interchangeable: a
   * cache hit from a chips-only fetch would otherwise serve a caller that asked
   * for rows a batch with none, and it would read as "no address is known".
   * Callers sharing a ref set should therefore agree on this, or they get a
   * request each.
   */
  includeRow?: boolean;
  /**
   * Resolve at an explicit tier instead of the caller's configured default.
   *
   * For a surface with its own widen control. Everything else should leave this
   * alone and inherit Settings › Default view scope, so the same address reads
   * the same way wherever it appears.
   */
  scope?: AddressScope;
}

export interface ResolvedAddressOverlaysBatchResult {
  batch: BatchOverlays;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  /** True when overlays are enabled AND at least one ref was queried. */
  active: boolean;
  /** The neutral decoration seam the transpose adapters (graph/scene) consume. */
  resolveOverlay: (address: string, chainId?: string | null) => AddressOverlay | undefined;
  refetch: () => void;
}

/**
 * The query key one batch read is cached under.
 *
 * Exported because the GraphQL address list can now answer this question as a
 * by-product of the page it was already fetching, and seeds this cache with the
 * result. A seed has to spell the key exactly as the read does — write it
 * differently and the entry is simply never found, which looks like nothing at
 * all rather than like a bug.
 *
 * The tier is part of the key: two tiers over the same refs are different
 * answers, and serving one for the other would show a teammate's view as your
 * own. So is `includeRow` — a batch fetched without it has empty row maps by
 * contract, so the two are not interchangeable.
 */
export function addressOverlaysBatchQueryKey(
  refs: OverlayRef[],
  includeRow: boolean,
  scope: AddressScope | undefined,
): (string | boolean | null)[] {
  return ["address-overlays-batch", refsCacheKey(dedupeRefs(refs)), includeRow, scope ?? null];
}

export function useResolvedAddressOverlaysBatch(
  refs: OverlayRef[],
  options: UseResolvedAddressOverlaysBatchOptions = {},
): ResolvedAddressOverlaysBatchResult {
  const { resource = "address", enabled = true, resolveLogoUrl, includeRow = false } = options;
  const config = overlayConfig(resource);

  // No explicit tier → the caller's default (Settings › Default view scope),
  // else the product default. `USER` IS the un-widened read, so it maps back to
  // undefined to keep the request body — and its cache key — identical to the
  // plain default read. Mirrors the single-address hook exactly; the two
  // disagreeing about the same setting is the bug this closes.
  const { resolveDefaultResolveScope } = useScopePrefs();
  const effectiveScope = options.scope ?? resolveDefaultResolveScope(resource);
  const scope: AddressScope | undefined =
    effectiveScope === "USER" ? undefined : effectiveScope;

  // Dedupe once; the stable key drives both the query cache and the gate.
  const deduped = React.useMemo(() => dedupeRefs(refs), [refs]);

  const on = enabled && config.enabled && config.resolvedView && deduped.length > 0;
  const client = useApiClient();

  const query = useQuery({
    queryKey: addressOverlaysBatchQueryKey(deduped, includeRow, scope),
    enabled: on,
    queryFn: ({ signal }) =>
      fetchAddressOverlaysBatch(client, deduped, { signal, includeRow, scope }),
  });

  const batch = query.data ?? EMPTY_BATCH_OVERLAYS;

  const resolveOverlay = React.useCallback(
    (address: string, chainId?: string | null): AddressOverlay | undefined =>
      overlayToAddressDecoration(lookupOverlays(batch, address, chainId), { resolveLogoUrl }),
    [batch, resolveLogoUrl],
  );

  return React.useMemo(
    () => ({
      batch,
      isLoading: query.isLoading && on,
      isFetching: query.isFetching,
      error: query.error,
      active: on,
      resolveOverlay,
      refetch: () => void query.refetch(),
    }),
    [batch, on, resolveOverlay, query],
  );
}
